import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Grid, Box, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import { Checkbox } from "@mui/material";
import {
  ModernLiquidTable,
  LiquidChip,
  LiquidIconButton,
} from "../../../components/modern-table";
import { Portlet, PortletBody } from "../../../partials/content/Portlet";
import {
  getAllMembers,
  getMembersByBranch,
  getMembersByStatus,
  updateMemberContacted,
} from "../../../api/member";
import * as qs from "query-string";
import exportToExcel from "../../../utils/exportToExcel";

export default function MemberListPage() {
  const location = useLocation();
  const { userType, branch } = useSelector(({ auth }) => auth.user);
  const [rows, setRows] = React.useState([]);
  const [memberStatus, setMemberStatus] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const br = userType === 0 ? undefined : branch;

  React.useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const parsed = qs.parse(location.search);
        setMemberStatus(parsed.status);
        const data =
          parsed.status && parsed.status !== "undefined"
            ? await getMembersByStatus(parsed.status, br)
            : br
            ? await getMembersByBranch(br)
            : await getAllMembers();
        if (data) {
          setRows(data);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [location.search, br]);

  const handleContactedChange = async (id, currentValue) => {
    try {
      await updateMemberContacted(id, !currentValue);
      setRows((prev) =>
        prev.map((row) =>
          row.id === id ? { ...row, contacted: !currentValue } : row,
        ),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getMemberStatus = (status) => {
    switch (status) {
      case "PENDING":
        return "Belum Bayar";
      case "PAID":
        return "Baru";
      case "ACTIVE":
        return "Aktif";
      case "ALUMNI":
        return "Alumni";
      case "REJECTED":
        return "Tidak diluluskan";
      case "EXPIRED":
        return "Tamat Tempoh";
      default:
        return "Tiada Maklumat";
    }
  };

  const columns = [
    {
      field: "_rowNumber",
      title: "No",
      width: 50,
      render: (rowData) => <p>{rowData._rowNumber}</p>,
    },
    { field: "fullname", title: "Nama", width: 300 },
    {
      field: "membershipId",
      title: "No Ahli",
    },
    { field: "branch", title: "Cawangan Gema" },
    {
      field: "memberStatus",
      title: "Status",
      render: (rowData) => {
        const status = rowData.memberStatus;
        let statusClass = "default";

        switch (status) {
          case "ACTIVE":
            statusClass = "active";
            break;
          case "PENDING":
            statusClass = "pending";
            break;
          case "EXPIRED":
            statusClass = "expired";
            break;
          case "PAID":
            statusClass = "success";
            break;
          default:
            statusClass = "default";
        }

        return (
          <LiquidChip
            label={getMemberStatus(status)}
            className={`status-${statusClass}`}
          />
        );
      },
    },
    ...(memberStatus === "2"
      ? [
          {
            field: "contacted",
            title: "Dihubungi",
            width: 120,
            render: (rowData) => (
              <Checkbox
                checked={!!rowData.contacted}
                onChange={() =>
                  handleContactedChange(rowData.id, rowData.contacted)
                }
                size="small"
              />
            ),
          },
        ]
      : []),
    ...(memberStatus === "4"
      ? [
          {
            field: "remarks",
            title: "Catatan",
          },
        ]
      : []),
    {
      field: "id",
      title: "Tindakan",
      width: 100,
      render: (rowData) => (
        <Tooltip title="Edit">
          <Link to={`/member/edit?id=${rowData.id}`}>
            <LiquidIconButton size="small">
              <EditIcon />
            </LiquidIconButton>
          </Link>
        </Tooltip>
      ),
    },
  ];

  const handleExport = (columns, data) => {
    const st = data.map((member) => {
      return {
        No_Ahli: member.membershipId,
        Cawangan: member.branch,
        Nama: member.fullname,
        No_IC: member.nationalId,
        Umur: member.age,
        No_Telefon: member.phone,
        Emel: member.email,
        Alamat: member.address,
        Bandar: member.city,
        Jantina: member.gender,
        Pekerjaan: member.occupation,
        Pendidikan: member.education,
        Agama: member.religion,
        Bangsa: member.race,
        Tarik_Mula: member.startDate,
        Tarikh_Tamat: member.endDate,
      };
    });
    exportToExcel(st);
  };

  const handleRefresh = async () => {
    const parsed = qs.parse(location.search);
    setLoading(true);
    try {
      const data =
        parsed.status && parsed.status !== "undefined"
          ? await getMembersByStatus(parsed.status, br)
          : br
          ? await getMembersByBranch(br)
          : await getAllMembers();
      if (data) {
        setRows(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Portlet>
      <PortletBody fluid={true}>
        <Grid className="p-3" container spacing={3}>
          <Grid item xs={12}>
            <Box
              sx={{
                mb: 2,
                p: 2,
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                borderRadius: "12px",
                backdropFilter: "blur(10px)",
              }}
            >
              <span className="kt-section__sub">
                Senarai di bawah menrangkumi ahli yang telah mendaftar melalui
                sistem sigma.
              </span>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <ModernLiquidTable
              columns={columns}
              data={rows}
              title={`Bilangan ahli: ${rows.length}`}
              loading={loading}
              onRefresh={handleRefresh}
              exportFunction={handleExport}
              emptyStateMessage="Tiada ahli berdaftar"
              options={{
                selection: false,
                actionsColumnIndex: -1,
                pageSize: 10,
                pageSizeOptions: [5, 10, 20, 50, 100],
              }}
            />
          </Grid>
        </Grid>
      </PortletBody>
    </Portlet>
  );
}
